import { Search, X } from "lucide-react";
import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "../lib/cn";

const base =
  "w-full rounded-sm border border-border bg-surface text-fg placeholder:text-fg-subtle " +
  "focus:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent/15 " +
  "disabled:opacity-50 disabled:cursor-not-allowed aria-[invalid=true]:border-err";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Monospace for identifiers, cron, SQL fragments. */
  mono?: boolean;
  inputSize?: "md" | "lg";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, mono, inputSize = "lg", ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        base,
        inputSize === "lg" ? "h-[32px] px-2.5 text-base" : "h-[28px] px-2 text-sm",
        mono && "font-mono text-sm",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }
>(({ className, mono, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      base,
      "min-h-[72px] px-2.5 py-1.5 text-base",
      mono && "font-mono text-sm",
      className,
    )}
    {...props}
  />
));
Textarea.displayName = "Textarea";

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string;
  onValueChange: (value: string) => void;
  /** Optional trailing text, e.g. "12 matches" or "⌘K". */
  trailing?: string;
  inputSize?: "md" | "lg";
}

/** Search field with leading icon and clear button; the workhorse of every filter bar. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      value,
      onValueChange,
      trailing,
      inputSize = "md",
      placeholder = "Search…",
      ...props
    },
    ref,
  ) => (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-sm border border-border bg-surface px-2 text-fg-subtle " +
          "focus-within:border-accent focus-within:ring-[3px] focus-within:ring-accent/15",
        inputSize === "lg" ? "h-[32px]" : "h-[28px]",
        className,
      )}
    >
      <Search className="size-3.5 shrink-0" aria-hidden />
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle [&::-webkit-search-cancel-button]:hidden"
        {...props}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onValueChange("")}
          aria-label="Clear search"
          className="rounded-xs p-0.5 hover:bg-surface-inset hover:text-fg"
        >
          <X className="size-3" aria-hidden />
        </button>
      ) : trailing ? (
        <span className="font-mono text-xs whitespace-nowrap">{trailing}</span>
      ) : null}
    </div>
  ),
);
SearchInput.displayName = "SearchInput";
